import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ContactoPersona } from '../../../domain/model/persona/contacto-persona';
import { BusquedaPaginadorComponent } from '../busquedas/busqueda-paginador.component';
import {
  sanitizeTextPreserveSpaces,
  sanitizeEmailContacto,
  emailKeypress,
  sanitizeNumbersOnly,
  onlyNumbersKeypress,
  CONTACT_MAX_LENGTHS
} from '../../../common/input-utils';
import { buscadorContactosPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Buscador de contactos reutilizable con paginación server-side y CRUD básico.
 */
@Component({
  selector: 'app-buscador-contactos-present',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../styles/grid-table.styles.scss'],
  styles: [
    `
      :host ::ng-deep .contactos-page-size-dropdown {
        width: 100%;
        max-width: 100%;
      }

      @media (min-width: 640px) {
        :host ::ng-deep .contactos-page-size-dropdown {
          width: 6rem;
          min-width: 6rem;
        }
      }

      :host ::ng-deep .contactos-page-size-panel {
        min-width: 6rem;
        max-height: min(240px, calc(100vh - 16px)) !important;
        overflow-y: auto;
        transform: translateY(calc(-100% - 4px)) !important;
        transform-origin: bottom left;
      }
    `
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      (onHide)="visibleChange.emit(false)"
      header="Buscar Contactos"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '64rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '90vw', '768px': '94vw', '640px': '96vw' }"
      appendTo="body"
    >
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div class="flex items-center gap-2"></div>
        <div>
          <button
            pButton
            type="button"
            label="Nuevo"
            icon="pi pi-plus"
            (click)="triggerOpenCreate()"
            class="p-button-sm min-h-[44px]"
            pTooltip="Crear nuevo contacto"
            tooltipPosition="left"
          ></button>
        </div>
      </div>

      <div class="md:hidden space-y-3 p-3">
        <div
          *ngIf="!loading && contactos.length === 0"
          class="text-center p-4 text-gray-500"
        >
          No se recuperó información con los parámetros ingresados.
        </div>
        <div
          *ngFor="let c of contactos; trackBy: trackContacto"
          class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
        >
          <div class="mb-3 min-w-0">
            <div class="text-xs text-gray-500 dark:text-gray-400">Nombre</div>
            <div
              class="font-semibold text-gray-800 dark:text-gray-100 truncate"
              [attr.title]="tituloTabla(c.dsnombre)"
            >
              {{ textoTabla(c.dsnombre) }}
            </div>
          </div>
          <div class="grid grid-cols-1 gap-2 text-sm mb-3">
            <div>
              <span class="text-gray-500 dark:text-gray-400">Cargo:</span>
              <span class="text-gray-800 dark:text-gray-200">{{
                textoTabla(c.dscargo || c.dscargo_desc)
              }}</span>
            </div>
            <div>
              <span class="text-gray-500 dark:text-gray-400">Teléfono fijo:</span>
              <span class="text-gray-800 dark:text-gray-200">{{
                textoTabla(c.dstelefono)
              }}</span>
            </div>
            <div>
              <span class="text-gray-500 dark:text-gray-400">Teléfono celular:</span>
              <span class="text-gray-800 dark:text-gray-200">{{
                textoTabla(c.dscelular)
              }}</span>
            </div>
            <div>
              <span class="text-gray-500 dark:text-gray-400">Email:</span>
              <span class="text-gray-800 dark:text-gray-200">{{
                textoTabla(c.dsemail)
              }}</span>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <p-button
              label="Seleccionar"
              icon="pi pi-check"
              size="small"
              styleClass="w-full min-h-[44px]"
              (onClick)="seleccionar(c)"
            ></p-button>
            <p-button
              label="Editar"
              icon="pi pi-pencil"
              severity="secondary"
              [outlined]="true"
              size="small"
              styleClass="w-full min-h-[44px]"
              (onClick)="onEditClick(c)"
            ></p-button>
          </div>
        </div>
      </div>

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="tableConfig.a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="contactos"
          [rows]="pageSize"
          [loading]="loading"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [tableStyle]="{
            'min-width': tableConfig.responsive.minWidth || 'max-content'
          }"
          [scrollHeight]="tableConfig.responsive.scrollHeight || '300px'"
          [styleClass]="tableStyleClass"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn>
                <span class="grid-header-text" title="Nombre">Nombre</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Cargo">Cargo</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Teléfono fijo"
                  >Teléfono fijo</span
                >
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Teléfono celular"
                  >Teléfono celular</span
                >
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Email">Email</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Acciones">Acciones</span>
              </th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-c>
            <tr>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(c.dsnombre)"
                  >{{ textoTabla(c.dsnombre) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(c.dscargo || c.dscargo_desc)"
                  >{{ textoTabla(c.dscargo || c.dscargo_desc) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(c.dstelefono)"
                  >{{ textoTabla(c.dstelefono) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(c.dscelular)"
                  >{{ textoTabla(c.dscelular) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(c.dsemail)"
                  >{{ textoTabla(c.dsemail) }}</span
                >
              </td>
              <td>
                <div class="flex items-center gap-2 whitespace-nowrap">
                  <button
                    pButton
                    type="button"
                    icon="pi pi-check"
                    class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                    (click)="seleccionar(c)"
                    pTooltip="Seleccionar"
                    tooltipPosition="top"
                  ></button>
                  <button
                    pButton
                    type="button"
                    icon="pi pi-pencil"
                    class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                    (click)="onEditClick(c)"
                    pTooltip="Editar"
                    tooltipPosition="top"
                  ></button>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center p-4 text-gray-500">
                No se recuperó información con los parámetros ingresados.
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <!-- Paginación -->
      <div
        *ngIf="
          totalRecords > 0 ||
          hasNext ||
          hasPrevious ||
          contactos.length >= pageSize
        "
        class="mt-2"
      >
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Contactos</span>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            Registros del {{ registroInicio }} al {{ registroFin }} de
            {{ totalRecords }}
          </span>
        </div>
        <app-busqueda-paginador
          [currentPage]="currentPage"
          [totalPages]="resolvedTotalPages"
          [hasPrevious]="canGoPrevious"
          [hasNext]="canGoNext"
          [pageSizeOptions]="pageSizeOptionValues"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="onPageSizeValueChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="irPaginaAnterior()"
          (next)="irPaginaSiguiente()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      </div>
    </p-dialog>

    <!-- Dialogo Crear/Editar -->
    <p-dialog
      header="Contacto"
      [(visible)]="editingVisible"
      (onHide)="editingVisibleChange.emit(false)"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '36rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      appendTo="body"
    >
      <form [formGroup]="contactForm" class="grid grid-cols-1 gap-3 w-full">
        <div>
          <label class="block text-sm font-medium w-full"
            >Nombre <span class="text-red-600 ml-1">*</span></label
          >
          <input
            pInputText
            formControlName="dsnombre"
            [maxlength]="maxLengths.NOMBRE"
            (change)="sanitizeTextControl('dsnombre')"
            class="w-full"
            aria-required="true"
          />
        </div>
        <div>
          <label class="block text-sm font-medium w-full">Cargo</label>
          <input
            pInputText
            formControlName="dscargo"
            [maxlength]="maxLengths.CARGO"
            (change)="sanitizeTextControl('dscargo')"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium w-full"
            >Teléfono <span class="text-red-600 ml-1">*</span></label
          >
          <input
            pInputText
            formControlName="dstelefono"
            [maxlength]="maxLengths.TELEFONO"
            (keypress)="onlyNumbersKeypress($event)"
            (change)="sanitizePhoneControl('dstelefono')"
            class="w-full"
            aria-required="true"
          />
        </div>
        <div>
          <label class="block text-sm font-medium w-full">Celular</label>
          <input
            pInputText
            formControlName="dscelular"
            [maxlength]="maxLengths.TELEFONO"
            (keypress)="onlyNumbersKeypress($event)"
            (change)="sanitizePhoneControl('dscelular')"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium w-full"
            >Email <span class="text-red-600 ml-1">*</span></label
          >
          <input
            pInputText
            formControlName="dsemail"
            [maxlength]="maxLengths.EMAIL"
            (keypress)="emailKeypress($event)"
            (change)="sanitizeEmailControl('dsemail')"
            class="w-full"
            aria-required="true"
          />
        </div>
      </form>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="editingVisible = false"
          ></p-button>
          <p-button
            label="Guardar"
            icon="pi pi-check"
            styleClass="min-h-[44px]"
            [loading]="guardando"
            [disabled]="guardando"
            (onClick)="triggerSaveContact()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BuscadorContactosComponent implements OnChanges {
  @Input() contactos: ContactoPersona[] = [];
  @Input() loading = false;
  @Input() guardando = false;
  @Input() totalRecords = 0;
  @Input() pageSize = 10;
  @Input() pageSizeOptions: Array<{ label: string; value: number }> = [];
  @Input() currentPage = 1;
  @Input() totalPages = 0;
  @Input() visible = false;
  @Input() editingVisible = false;
  @Input() contactForm!: FormGroup;
  @Input() editMode = false;
  @Input() editingContactId?: number | null = null;
  @Input() hasNext = false;
  @Input() hasPrevious = false;

  @Output() selectContact = new EventEmitter<ContactoPersona>();
  @Output() openCreate = new EventEmitter<void>();
  @Output() editContact = new EventEmitter<ContactoPersona>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() goToPage = new EventEmitter<number>();
  @Output() saveContactEvent = new EventEmitter<any>();

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() editingVisibleChange = new EventEmitter<boolean>();

  selectedPageSize = 0;

  readonly tableConfig = buscadorContactosPreset.createConfig({
    style: {
      tableClassName: 'tabla-buscador-contactos',
      striped: true,
      gridlines: false,
      resizableColumns: true,
      rowHover: false
    },
    responsive: {
      mode: 'hybrid',
      minWidth: 'max-content',
      scrollHeight: '300px',
      cardBreakpointPx: 768
    },
    a11y: {
      tableAriaLabel: 'Tabla de busqueda de contactos',
      announceSelection: true,
      announcePageChanges: true,
      focusFirstActionOnRowSelect: false
    }
  });

  // Expose sanitizers to template
  sanitizeTextPreserveSpaces = sanitizeTextPreserveSpaces;
  sanitizeEmailContacto = sanitizeEmailContacto;
  emailKeypress = emailKeypress;
  sanitizeNumbersOnly = sanitizeNumbersOnly;
  readonly maxLengths = CONTACT_MAX_LENGTHS;
  onlyNumbersKeypress = onlyNumbersKeypress;

  constructor() {
    this.selectedPageSize = this.pageSize;
  }

  get tableStyleClass(): string {
    const classes = ['p-datatable-sm', this.tableConfig.style.tableClassName];
    if (this.tableConfig.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (this.tableConfig.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageSize']) {
      this.selectedPageSize = this.pageSize;
    }
  }

  get resolvedTotalPages(): number {
    if (this.totalPages > 0) {
      return this.totalPages;
    }
    if (this.totalRecords > 0 && this.pageSize > 0) {
      return Math.ceil(this.totalRecords / this.pageSize);
    }
    return 0;
  }

  get canGoPrevious(): boolean {
    return this.currentPage > 1 || this.hasPrevious;
  }

  get pageSizeOptionValues(): number[] {
    const values = this.pageSizeOptions
      .map((option) => Number(option.value))
      .filter((value) => Number.isFinite(value) && value > 0);

    return values.length > 0 ? values : [this.pageSize];
  }

  get canGoNext(): boolean {
    if (this.currentPage < this.resolvedTotalPages) {
      return true;
    }

    const shownUntil = this.currentPage * this.pageSize;
    if (this.totalRecords > shownUntil) {
      return true;
    }

    return this.hasNext;
  }

  get registroInicio(): number {
    if (this.totalRecords <= 0) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get registroFin(): number {
    if (this.totalRecords <= 0) {
      return 0;
    }
    const fin = this.registroInicio + this.contactos.length - 1;
    return Math.min(fin, this.totalRecords);
  }

  abrir(params?: {
    cdpersona?: number;
    nmconsecCentroTrabajo?: number;
  }) {
    // Presentational component stays dumb. Kept for compatibility.
  }

  cerrar() {
    // Container controls visibility.
  }

  seleccionar(c: ContactoPersona) {
    this.selectContact.emit(c);
  }

  triggerOpenCreate(): void {
    this.openCreate.emit();
  }

  onEditClick(c: ContactoPersona): void {
    this.editContact.emit(c);
  }

  async triggerSaveContact(): Promise<void> {
    if (!this.contactForm) return;
    this.contactForm.markAllAsTouched();
    const f = this.contactForm.value;
    this.saveContactEvent.emit({
      ...f,
      editMode: this.editMode,
      existingId: this.editingContactId
    });
  }

  sanitizeTextControl(field: string): void {
    const v = this.contactForm.get(field)?.value;
    this.contactForm
      .get(field)
      ?.setValue(sanitizeTextPreserveSpaces(String(v || '')), {
        emitEvent: false
      });
  }

  sanitizeEmailControl(field: string): void {
    const v = this.contactForm.get(field)?.value;
    this.contactForm
      .get(field)
      ?.setValue(sanitizeEmailContacto(String(v || '')), { emitEvent: false });
  }

  sanitizePhoneControl(field: string): void {
    const v = this.contactForm.get(field)?.value;
    this.contactForm
      .get(field)
      // Legacy: teléfono fijo y celular con REGEX_JUST_NUMBERS (solo dígitos)
      ?.setValue(sanitizeNumbersOnly(String(v || '')), { emitEvent: false });
  }

  irPrimeraPagina(): void {
    this.goToPage.emit(1);
  }

  irPaginaAnterior(): void {
    if (!this.canGoPrevious) return;
    const cur = this.currentPage;
    if (cur <= 1) return;
    this.goToPage.emit(cur - 1);
  }

  irPaginaSiguiente(): void {
    if (!this.canGoNext) return;
    const cur = this.currentPage;
    this.goToPage.emit(cur + 1);
  }

  irUltimaPagina(): void {
    if (this.resolvedTotalPages > 0) {
      this.goToPage.emit(this.resolvedTotalPages);
    }
  }

  onPageSizeChange(): void {
    const v = Number(this.selectedPageSize) || this.pageSize;
    this.pageSizeChange.emit(v);
  }

  onPageSizeValueChange(pageSize: number): void {
    this.selectedPageSize = pageSize;
    this.onPageSizeChange();
  }

  trackContacto = (_: number, c: ContactoPersona): string | number =>
    c.nmconsecutivo ?? `${c.dsnombre}-${c.dsemail}`;

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
